import { uniqBy } from 'lodash-es';
import { Delta } from 'quill/core';
import React from 'react';
import deltaToText from '../editor/utils/deltaToText';
import { gql } from '../graphql/generated/gql';
import type { DocumentType } from '../graphql/generated/gql';
import { TopicPrivacy } from '../graphql/generated/graphql';
import privateIcon from '../icons/non-prefilled/16x16/private.svg';
import publicIcon from '../icons/non-prefilled/16x16/public.svg';
import secretIcon from '../icons/non-prefilled/16x16/secret.svg';
import topicIcon from '../icons/prefilled/16x16/topic.svg';
import TopicBreadcrumbs from '../topic/TopicBreadcrumbs';
import { useGetUser } from '../user/useAllUsers';
import topicPath from '../utils/url/topicPath';
import AvatarStack from './AvatarStack';
import SquarishCardBase from './SquarishCardBase';
import * as styles from './TopicSquarishCard.module.scss';

const TOPIC_FRAGMENT = gql(`
  fragment TopicSquarishCard_topic on Topic {
    id
    name
    description
    privacy
    banner {
      original
    }
    owners {
      id
      ...useGetUser_user
    }
    members {
      id
      ...useGetUser_user
    }
    ancestors {
      ...TopicBreadcrumbs_topics
    }
    ...topicPath_topic
  }
`);

const privacyIconMap: Record<TopicPrivacy, string | null> = {
  [TopicPrivacy.Public]: publicIcon,
  [TopicPrivacy.Private]: privateIcon,
  [TopicPrivacy.Secret]: secretIcon,
  [TopicPrivacy.Open]: null,
};

interface TopicSquarishCardProps {
  topic: DocumentType<typeof TOPIC_FRAGMENT>;
  overflowMenuItems?: React.ReactNode;
  link?: boolean;
}

const TopicSquarishCard = ({
  topic,
  link,
  overflowMenuItems,
}: TopicSquarishCardProps) => {
  const getUser = useGetUser();

  const users = uniqBy(
    [...topic.owners, ...topic.members],
    (item) => item.id,
  ).map(getUser);

  const privacyIcon = privacyIconMap[topic.privacy];

  return (
    <SquarishCardBase
      avatars={
        <AvatarStack
          items={users}
          countOverflowVariant="circle"
          limit={4}
          borderSize={3}
          size={26}
        />
      }
      banner={
        topic.banner?.original ? (
          <img
            alt="Topic banner"
            className={styles.bannerImage}
            src={topic.banner.original}
          />
        ) : (
          <div className={styles.plainBanner} />
        )
      }
      title={
        <>
          {!!topic.ancestors.length && (
            <div className={styles.breadcrumbs}>
              <TopicBreadcrumbs
                topics={topic.ancestors}
                truncate
                size="small"
              />
            </div>
          )}
          <h4 className={styles.topicName}>{topic.name}</h4>
        </>
      }
      description={deltaToText(new Delta(topic.description))}
      icons={
        <>
          <span className={styles.topicIcon}>
            <SquarishCardBase.Icon icon={topicIcon} />
          </span>
          {!!privacyIcon && <SquarishCardBase.Icon icon={privacyIcon} />}
        </>
      }
      overflowMenuItems={overflowMenuItems}
      linkTo={link ? topicPath(topic) : undefined}
    />
  );
};

export default TopicSquarishCard;
